import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { cache } from '@/lib/cache';

const EXPERIMENTS_DASHBOARD_ITEMS_CACHE_TTL = 30 * 60 * 1000; // 30 min
const EXPERIMENTS_DASHBOARD_ROW_CACHE_TTL = 10 * 60 * 1000;   // 10 min

export type LayoutType = 'span1' | 'span2' | 'span3' | 'span4' | 'span5' | 'span6' | 'span7' | 'span8' | 'span9';

export type DashboardType = 'dashboard' | 'controls' | 'widgets';

export interface Dashboard {
  id: string;
  brand_id: string | null;
  name: string;
  description: string | null;
  type?: DashboardType;
  user_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  /** When true, builder shows widget previews in compact (overlay) mode */
  preview_compact?: boolean;
  /** Joined from brands when available */
  brands?: { name: string } | null;
}

export interface DashboardItem {
  id: string;
  dashboard_id: string;
  component_key: string;
  label: string;
  display_name: string | null;
  category: string;
  route: string;
  layout: LayoutType;
  order: number;
  width: number | null;
  height: number | null;
  x: number | null;
  y: number | null;
  created_at: string;
}

export function useDashboards(
  brandId: string | undefined,
  typeFilter?: DashboardType,
  options?: { allBrandIds?: string[] }
) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const allBrandIds = options?.allBrandIds?.filter(Boolean) ?? [];
  const fetchAllBrands = allBrandIds.length > 0;

  const { data: dashboards, isLoading } = useQuery({
    queryKey: ['dashboards', fetchAllBrands ? 'all' : brandId, typeFilter, fetchAllBrands ? allBrandIds : null, user?.id, isSuperAdmin],
    queryFn: async () => {
      const uid = user?.id ?? null;
      let query = supabase
        .from('os_experiments_dashboards')
        .select('*, brands(name)');

      if (fetchAllBrands) {
        const baseSelect = '*, brands(name)';
        const orderOpt = { ascending: false as const };
        const includeAllUsers = isSuperAdmin;
        let brandQuery = supabase
          .from('os_experiments_dashboards')
          .select(baseSelect)
          .in('brand_id', allBrandIds);
        if (!includeAllUsers) {
          brandQuery = brandQuery.or(uid != null ? `user_id.eq.${uid},user_id.is.null` : 'user_id.is.null');
        }
        let globalQuery = supabase
          .from('os_experiments_dashboards')
          .select(baseSelect)
          .is('brand_id', null)
          .is('user_id', null);
        let userDefaultQuery: ReturnType<typeof supabase.from> | null = null;
        if (includeAllUsers) {
          userDefaultQuery = supabase.from('os_experiments_dashboards').select(baseSelect).is('brand_id', null).not('user_id', 'is', null);
        } else if (uid != null) {
          userDefaultQuery = supabase.from('os_experiments_dashboards').select(baseSelect).is('brand_id', null).eq('user_id', uid);
        }
        if (typeFilter) {
          brandQuery = brandQuery.eq('type', typeFilter);
          globalQuery = globalQuery.eq('type', typeFilter);
          if (userDefaultQuery) userDefaultQuery = userDefaultQuery.eq('type', typeFilter);
        }
        const queries: Promise<any>[] = [
          brandQuery.order('updated_at', orderOpt) as any,
          globalQuery.order('updated_at', orderOpt) as any,
        ];
        if (userDefaultQuery) queries.push(userDefaultQuery.order('updated_at', orderOpt));
        const results = await Promise.all(queries);
        const brandRes = results[0] as { data: any; error: any };
        const globalRes = results[1] as { data: any; error: any };
        const userDefaultRes = results[2] as { data: any; error: any } | undefined;
        if (brandRes.error) throw brandRes.error;
        if (globalRes.error) throw globalRes.error;
        if (userDefaultRes?.error) throw userDefaultRes.error;
        const combined = [
          ...(brandRes.data ?? []),
          ...(globalRes.data ?? []),
          ...(userDefaultRes?.data ?? []),
        ];
        combined.sort((a, b) => new Date((b as { updated_at: string }).updated_at).getTime() - new Date((a as { updated_at: string }).updated_at).getTime());
        return combined as Dashboard[];
      } else {
        if (!brandId) return [];
        if (isSuperAdmin) {
          query = query.or(`brand_id.eq.${brandId},brand_id.is.null`);
        } else {
          const clauses: string[] =
            uid != null
              ? [
                  `brand_id.eq.${brandId},user_id.eq.${uid}`,
                  `brand_id.eq.${brandId},user_id.is.null`,
                  'brand_id.is.null,user_id.is.null',
                  `brand_id.is.null,user_id.eq.${uid}`,
                ]
              : [
                  `brand_id.eq.${brandId},user_id.is.null`,
                  'brand_id.is.null,user_id.is.null',
                ];
          query = query.or(clauses.join(','));
        }
      }

      query = query.order('updated_at', { ascending: false });

      if (typeFilter) {
        query = query.eq('type', typeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Dashboard[];
    },
    enabled: !!brandId || fetchAllBrands,
    staleTime: 5 * 60 * 1000, // 5 min - cache like dashboard
  });

  const createDashboardMutation = useMutation({
    mutationFn: async (dashboard: Partial<Dashboard>) => {
      if (!brandId) throw new Error('No brand selected');
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;

      const type = dashboard.type ?? 'dashboard';
      const isPersonalOverlay = type === 'controls' || type === 'widgets';
      const user_id = dashboard.user_id ?? (isPersonalOverlay ? userId : null);
      const payload = {
        brand_id: brandId,
        name: dashboard.name || 'Untitled Dashboard',
        description: dashboard.description ?? null,
        type,
        user_id,
        created_by: userId,
      };

      const { data, error } = await supabase
        .from('os_experiments_dashboards')
        .upsert(payload, { onConflict: 'user_id,brand_id,type' })
        .select()
        .single();

      if (error) throw error;
      return data as Dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences'] });
      toast.success('Dashboard created');
    },
    onError: (error) => {
      toast.error('Failed to create dashboard: ' + (error as Error).message);
    },
  });

  const updateDashboardMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Dashboard> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_experiments_dashboards')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Dashboard;
    },
    onSuccess: (_, variables) => {
      if (variables.id) {
        cache.remove(`experiments-dashboard-${variables.id}`);
      }
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ['dashboard-by-id', variables.id] });
      }
      toast.success('Dashboard saved');
    },
    onError: (error) => {
      toast.error('Failed to save dashboard: ' + error.message);
    },
  });

  const deleteDashboardMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_experiments_dashboards')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, id) => {
      if (id) {
        cache.remove(`experiments-dashboard-${id}`);
        cache.remove(`experiments-dashboard-items-${id}`);
      }
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences'] });
      toast.success('Dashboard deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete dashboard: ' + (error as Error).message);
    },
  });

  /** Set dashboard to brand shared (user_id=null) or global (user_id=null, brand_id=null). Deletes conflicting row then updates. */
  const cloneDashboardMutation = useMutation({
    mutationFn: async ({
      sourceDashboardId,
      targetBrandId,
      targetUserId,
    }: {
      sourceDashboardId: string;
      targetBrandId: string;
      targetUserId: string | null;
    }) => {
      const { data: authData } = await supabase.auth.getUser();
      const createdBy = authData.user?.id ?? null;

      const { data: source, error: fetchErr } = await supabase
        .from('os_experiments_dashboards')
        .select('id, name, description, type')
        .eq('id', sourceDashboardId)
        .single();
      if (fetchErr || !source) throw new Error('Dashboard not found');

      const { data: items, error: itemsErr } = await supabase
        .from('os_experiments_dashboard_items')
        .select('*')
        .eq('dashboard_id', sourceDashboardId)
        .order('order', { ascending: true });
      if (itemsErr) throw itemsErr;

      const { data: newDashboard, error: insertErr } = await supabase
        .from('os_experiments_dashboards')
        .insert({
          brand_id: targetBrandId,
          user_id: targetUserId,
          name: `${(source as { name: string }).name} (Copy)`,
          description: (source as { description: string | null }).description ?? null,
          type: (source as { type: DashboardType }).type ?? 'dashboard',
          created_by: createdBy,
        })
        .select()
        .single();
      if (insertErr) throw insertErr;

      const newId = (newDashboard as Dashboard).id;
      if (items && items.length > 0) {
        const { error: insertItemsErr } = await supabase.from('os_experiments_dashboard_items').insert(
          items.map((item: DashboardItem) => ({
            dashboard_id: newId,
            component_key: item.component_key,
            label: item.label,
            display_name: item.display_name ?? null,
            category: item.category,
            route: item.route,
            layout: item.layout,
            order: item.order,
            width: item.width ?? null,
            height: item.height ?? null,
            x: item.x ?? null,
            y: item.y ?? null,
          }))
        );
        if (insertItemsErr) throw insertItemsErr;
      }

      return newDashboard as Dashboard;
    },
    onSuccess: (newDashboard, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences'] });
      toast.success('Dashboard cloned');
    },
    onError: (error) => {
      toast.error('Failed to clone dashboard: ' + (error as Error).message);
    },
  });

  const setDashboardScopeMutation = useMutation({
    mutationFn: async ({
      id,
      scope,
    }: {
      id: string;
      scope: 'shared' | 'global';
    }) => {
      if (!id || typeof id !== 'string') throw new Error('Dashboard id is required.');
      const { data: row, error: fetchError } = await supabase
        .from('os_experiments_dashboards')
        .select('type, brand_id')
        .eq('id', id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (!row) throw new Error('Dashboard not found or you don’t have permission to change its scope.');

      const { type } = row as { type: DashboardType; brand_id: string | null };
      const targetBrandId = scope === 'global' ? null : (row as { brand_id: string | null }).brand_id;

      if (scope === 'shared' && targetBrandId == null) throw new Error('Dashboard has no brand');

      const deleteQuery = supabase
        .from('os_experiments_dashboards')
        .delete()
        .is('user_id', null)
        .eq('type', type);
      const toDelete = scope === 'global' ? deleteQuery.is('brand_id', null) : deleteQuery.eq('brand_id', targetBrandId!);
      const { error: delError } = await toDelete.neq('id', id);
      if (delError) throw delError;

      const updates = scope === 'global' ? { user_id: null, brand_id: null } : { user_id: null };
      const { data: updated, error: updateError } = await supabase
        .from('os_experiments_dashboards')
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updated) throw new Error('Dashboard not found or you don’t have permission to change its scope.');
      return updated as Dashboard;
    },
    onSuccess: (_, { scope }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences'] });
      toast.success(scope === 'global' ? 'Set as global default' : 'Set to brand shared');
    },
    onError: (error) => {
      toast.error('Failed to update scope: ' + (error as Error).message);
    },
  });

  const setDashboardAsUserDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;
      if (!userId) throw new Error('Not authenticated');

      const { data: row, error: fetchError } = await supabase
        .from('os_experiments_dashboards')
        .select('id, type, user_id')
        .eq('id', id)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (!row) throw new Error('Dashboard not found');
      if ((row as { user_id: string | null }).user_id !== userId) {
        throw new Error('You can only set your own dashboards as user default');
      }

      const type = (row as { type: DashboardType }).type;

      const { error: delError } = await supabase
        .from('os_experiments_dashboards')
        .delete()
        .eq('user_id', userId)
        .is('brand_id', null)
        .eq('type', type)
        .neq('id', id);
      if (delError) throw delError;

      const { data: updated, error: updateError } = await supabase
        .from('os_experiments_dashboards')
        .update({ brand_id: null })
        .eq('id', id)
        .select()
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updated) throw new Error('Failed to update dashboard');
      return updated as Dashboard;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      queryClient.invalidateQueries({ queryKey: ['resolved-experiment-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences'] });
      toast.success('Set as user default');
    },
    onError: (error) => {
      toast.error('Failed to set as user default: ' + (error as Error).message);
    },
  });

  return {
    dashboards,
    isLoading,
    createDashboard: createDashboardMutation.mutateAsync,
    updateDashboard: updateDashboardMutation.mutateAsync,
    deleteDashboard: deleteDashboardMutation.mutateAsync,
    setDashboardScope: setDashboardScopeMutation.mutateAsync,
    setDashboardAsUserDefault: setDashboardAsUserDefaultMutation.mutateAsync,
    cloneDashboard: cloneDashboardMutation.mutateAsync,
  };
}

export interface CanvasItemInput {
  id: string;
  componentKey: string;
  label: string;
  displayName?: string | null;
  category: string;
  route: string;
  layout: LayoutType;
  order: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

/** Fetch a single dashboard by id (e.g. for overlay to read preview_compact). */
export function useDashboardById(dashboardId: string | null | undefined) {
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard-by-id', dashboardId],
    queryFn: async () => {
      if (!dashboardId) return null;
      const cacheKey = `experiments-dashboard-${dashboardId}`;
      const cached = cache.getWithMetadata<Pick<Dashboard, 'id' | 'preview_compact'> | null>(cacheKey);
      if (cached && !cache.isStale(cacheKey, EXPERIMENTS_DASHBOARD_ROW_CACHE_TTL)) {
        return cached.data;
      }
      const { data, error } = await supabase
        .from('os_experiments_dashboards')
        .select('id, preview_compact')
        .eq('id', dashboardId)
        .maybeSingle();
      if (error) throw error;
      const result = data as Pick<Dashboard, 'id' | 'preview_compact'> | null;
      cache.set(cacheKey, result, { ttl: EXPERIMENTS_DASHBOARD_ROW_CACHE_TTL });
      return result;
    },
    enabled: !!dashboardId,
    staleTime: 2 * 60 * 1000,
  });
  return { dashboard: dashboard ?? null };
}

export function useDashboardItems(dashboardId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: items, isLoading } = useQuery({
    queryKey: ['dashboard-items', dashboardId],
    queryFn: async () => {
      if (!dashboardId) return [];
      const cacheKey = `experiments-dashboard-items-${dashboardId}`;
      const cached = cache.getWithMetadata<DashboardItem[]>(cacheKey);
      if (cached && !cache.isStale(cacheKey, EXPERIMENTS_DASHBOARD_ITEMS_CACHE_TTL)) {
        return cached.data;
      }
      const { data, error } = await supabase
        .from('os_experiments_dashboard_items')
        .select('*')
        .eq('dashboard_id', dashboardId)
        .order('order', { ascending: true });

      if (error) throw error;
      const result = (data ?? []) as DashboardItem[];
      cache.set(cacheKey, result, { ttl: EXPERIMENTS_DASHBOARD_ITEMS_CACHE_TTL });
      return result;
    },
    enabled: !!dashboardId,
    staleTime: 5 * 60 * 1000, // 5 min
  });

  const saveItemsMutation = useMutation({
    mutationFn: async (canvasItems: CanvasItemInput[]) => {
      if (!dashboardId) throw new Error('No dashboard selected');

      await supabase
        .from('os_experiments_dashboard_items')
        .delete()
        .eq('dashboard_id', dashboardId);

      if (canvasItems.length > 0) {
        const { error } = await supabase.from('os_experiments_dashboard_items').insert(
          canvasItems.map((item) => ({
            dashboard_id: dashboardId,
            component_key: item.componentKey,
            label: item.label,
            display_name: item.displayName ?? null,
            category: item.category,
            route: item.route,
            layout: item.layout,
            order: item.order,
            width: item.width ?? null,
            height: item.height ?? null,
            x: item.x ?? null,
            y: item.y ?? null,
          }))
        );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      if (dashboardId) cache.remove(`experiments-dashboard-items-${dashboardId}`);
      queryClient.invalidateQueries({ queryKey: ['dashboard-items'] });
      toast.success('Dashboard saved');
    },
    onError: (error) => {
      toast.error('Failed to save dashboard: ' + error.message);
    },
  });

  return {
    items: items ?? [],
    isLoading,
    saveItems: saveItemsMutation.mutateAsync,
  };
}

/** Global experiments dashboard (type 'dashboard', brand_id null, user_id null). Used when no brand context. */
export function useGlobalExperimentDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['global-experiment-dashboard'],
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('os_experiments_dashboards')
        .select('id, name')
        .eq('type', 'dashboard')
        .is('user_id', null)
        .is('brand_id', null)
        .maybeSingle();
      if (error) throw error;
      return row as { id: string; name: string } | null;
    },
    staleTime: 2 * 60 * 1000,
  });

  return {
    globalDashboard: data ?? null,
    isLoading,
  };
}

export interface ResolvedExperimentDashboardResult {
  dashboard: { id: string; name: string } | null;
  mergeWithDashboardId?: string;
}

/**
 * Resolved experiment dashboard for the main /dashboard page.
 * Resolution order: user default → personal → brand shared → global.
 * When mergeUserDefaultWithGlobal is true and primary is user default and global exists, returns mergeWithDashboardId so overlay can merge items.
 */
export function useResolvedExperimentDashboard(
  brandId: string | undefined,
  options?: { mergeUserDefaultWithGlobal?: boolean }
) {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const mergeUserDefaultWithGlobal = options?.mergeUserDefaultWithGlobal ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['resolved-experiment-dashboard', brandId, mergeUserDefaultWithGlobal, uid],
    queryFn: async (): Promise<ResolvedExperimentDashboardResult> => {
      const [
        userDefaultRes,
        personalRes,
        brandSharedRes,
        globalRes,
      ] = await Promise.all([
        uid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id, name')
              .eq('type', 'dashboard')
              .is('brand_id', null)
              .eq('user_id', uid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        brandId && uid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id, name')
              .eq('type', 'dashboard')
              .eq('brand_id', brandId)
              .eq('user_id', uid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        brandId
          ? supabase
              .from('os_experiments_dashboards')
              .select('id, name')
              .eq('type', 'dashboard')
              .eq('brand_id', brandId)
              .is('user_id', null)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from('os_experiments_dashboards')
          .select('id, name')
          .eq('type', 'dashboard')
          .is('user_id', null)
          .is('brand_id', null)
          .maybeSingle(),
      ]);
      if ((userDefaultRes as any).error) throw (userDefaultRes as any).error;
      if ((personalRes as any).error) throw (personalRes as any).error;
      if ((brandSharedRes as any).error) throw (brandSharedRes as any).error;
      if ((globalRes as any).error) throw (globalRes as any).error;

      const userDefault = userDefaultRes.data as { id: string; name: string } | null;
      const personal = personalRes.data as { id: string; name: string } | null;
      const brandShared = brandSharedRes.data as { id: string; name: string } | null;
      const global = globalRes.data as { id: string; name: string } | null;

      const primary = userDefault ?? personal ?? brandShared ?? global;

      if (mergeUserDefaultWithGlobal && primary?.id === userDefault?.id && global && global.id !== primary.id) {
        return { dashboard: primary, mergeWithDashboardId: global.id };
      }
      return { dashboard: primary ?? null };
    },
    staleTime: 2 * 60 * 1000,
  });

  return {
    dashboard: data?.dashboard ?? null,
    mergeWithDashboardId: data?.mergeWithDashboardId,
    isLoading,
  };
}
